import React, { useState, useEffect } from 'react';
import { format, getDaysInMonth, parseISO } from 'date-fns';
import { Select, SelectWrapper, Label } from './styles';
import { Box } from '@/shared/ui/box';
import { BodyTextRegularPrimary } from '../typography';
import { useI18n } from '@locales/client';

const generateYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  const minYear = currentYear - 18; // Minimum year is current year - 18
  const maxYear = currentYear - 100; // Maximum year is current year - 100
  for (let year = minYear; year >= maxYear; year--) {
    years.push(year);
  }
  return years;
};

export const DatePicker = ({
  initialDate,
  onDateChange,
  label,
  input,
  disabled,
  ...rest
}) => {
  const [selectedYear, setSelectedYear] = useState(2000);
  const [selectedMonth, setSelectedMonth] = useState(1);
  const [selectedDay, setSelectedDay] = useState(1);
  const t = useI18n();

  const years = generateYears();
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  useEffect(() => {
    if (!initialDate) return;
    const date =
      typeof initialDate === 'string' ? parseISO(initialDate) : initialDate;

    setSelectedYear(date?.getFullYear());
    setSelectedMonth(date?.getMonth() + 1);
    setSelectedDay(date.getDate());
    if (input) {
      handleChange(date.getFullYear(), date.getMonth() + 1, date.getDate());
    }
  }, [initialDate]);

  const updateDate = (year, month, day) => {
    const formattedDate = format(new Date(year, month - 1, day), 'yyyy-MM-dd');

    if (input) {
      input?.onChange(formattedDate);
    } else {
      onDateChange(formattedDate);
    }
  };

  const handleChange = (year, month, day) => {
    const daysInMonth = getDaysInMonth(new Date(year, month - 1));
    const newDay = Math.min(day, daysInMonth);

    setSelectedYear(year);
    setSelectedMonth(month);
    setSelectedDay(newDay);
    updateDate(year, month, newDay);
  };

  const handleYearChange = (e) => {
    const newYear = Number(e.target.value);
    handleChange(newYear, selectedMonth, selectedDay);
  };

  const handleMonthChange = (e) => {
    const newMonth = Number(e.target.value);
    handleChange(selectedYear, newMonth, selectedDay);
  };

  const handleDayChange = (e) => {
    const newDay = Number(e.target.value);
    handleChange(selectedYear, selectedMonth, newDay);
  };

  const days = Array.from(
    { length: getDaysInMonth(new Date(selectedYear, selectedMonth - 1)) },
    (_, i) => i + 1
  );

  return (
    <Box $display={'flex'} $flexDirection={'column'} $textAlign={'left'}>
      <Box $marginLeft={4} $marginBottom={[0, 4]}>
        <BodyTextRegularPrimary color="#AFAFAF">
          {label && <Label>{label}</Label>}
        </BodyTextRegularPrimary>
      </Box>
      <Box $display={'flex'} $gap={4} $flexWrap="wrap">
        <SelectWrapper>
          <Select
            value={selectedMonth}
            onChange={handleMonthChange}
            disabled={disabled}
          >
            {months.map((month) => (
              <option key={month} value={month}>
                {format(new Date(0, month - 1), 'MM')}
              </option>
            ))}
          </Select>
        </SelectWrapper>
        <SelectWrapper>
          <Select
            value={selectedDay}
            onChange={handleDayChange}
            disabled={disabled}
          >
            {days.map((day) => (
              <option key={day} value={day}>
                {day.toString().padStart(2, '0')}
              </option>
            ))}
          </Select>
        </SelectWrapper>

        <SelectWrapper>
          <Select
            value={selectedYear}
            onChange={handleYearChange}
            disabled={disabled}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Select>
        </SelectWrapper>
      </Box>
    </Box>
  );
};
